import { t } from 'i18next';
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';

import { ConfigurationDto } from '@/api-sdk';
import { iconChevronLeft } from '@/components/icons';
import {
  global,
  GlobalComponentType,
  MobileContext,
  personalisation,
  PersonalisationType,
  printExample,
  PrintExampleContextType,
} from '@/context';
import {
  ANIMATION_SPEED,
  ANIMATION_SPEED_FAST,
  BREAKPOINT_MEDIUM_PX,
} from '@/settings';
import { baseStyles, buttonStyles, rootVariablesStyles } from '@/styles';
import { ActionEvent, Step } from '@/types';
import { saveConfiguration, sendCTAClickEvent } from '@/utils';
import { consume } from '@lit/context';

@customElement('fg-configurator-mobile-navigation')
export class FgConfiguratorMobileNavigation extends LitElement {
  /**
   * Indicate what step is currently active
   */
  @property({ type: Number })
  currentStep = 0;

  /**
   * Used to determine how many steps are available
   */
  @property({ type: Array })
  steps: Step[] = [];

  /**
   * Used to determine if the current step is valid, to enable or disable the navigation buttons
   */
  @property({ type: Object })
  validSteps: { [key: string]: boolean } = {};

  @property({ type: Boolean })
  quantityValidated = false;

  @property({ type: Object })
  configuration?: ConfigurationDto | null;

  @state()
  progress = 1;

  @state()
  progressBarThreshold = 0;

  @state()
  error: boolean = false;

  @consume({ context: global.context })
  globalState!: GlobalComponentType;

  @consume({ context: MobileContext, subscribe: true })
  isMobile: boolean = false;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext: PersonalisationType[] = [];

  @consume({ context: printExample.context, subscribe: true })
  printExampleContext?: PrintExampleContextType;

  prevStep() {
    this.dispatchEvent(new CustomEvent('prev-step', { composed: true }));
  }

  nextStep() {
    this.dispatchEvent(new CustomEvent('next-step', { composed: true }));
  }

  async addToCartWithConfiguration() {
    if (!this.configuration) {
      return;
    }

    this.error = false;

    try {
      await saveConfiguration({
        globalContext: this.globalState,
        personalisationContext: this.personalisationContext,
        printExampleContext: this.printExampleContext?.service,
        configuration: this.configuration,
        callback: configurationId => {
          this.dispatchEvent(
            new CustomEvent('add-to-cart', {
              composed: true,
              detail: {
                configuration: configurationId,
              },
            }),
          );
        },
      });
    } catch {
      this.error = true;
    }
  }

  render() {
    this.progress = this.currentStep + 1;
    // Add price details step to threshold count
    this.progressBarThreshold = this.steps.length + 1;

    const { layout } = this.globalState;

    return this.isMobile
      ? html`
          <div class="progress-bar">
            <span
              style=${`width: ${
                (this.progress / this.progressBarThreshold) * 100
              }%`}
              class="progress"
            ></span>
          </div>
          <div
            class="buttons-wrapper ${this.currentStep === this.steps.length &&
            layout === 'retail'
              ? 'hidden'
              : 'show'}"
          >
            ${(this.currentStep > 0 &&
              this.progress < this.progressBarThreshold &&
              layout === 'retail') ||
            (layout === 'wholesale' && this.currentStep > 1)
              ? html`
                  <button
                    class="${layout === 'retail'
                      ? 'button-tertiary'
                      : 'button-link'} previous-button"
                    @click=${this.prevStep}
                  >
                    <div class="previous-icon">${iconChevronLeft}</div>
                    <span class="previous-text">${t('actions.previous')}</span>
                  </button>
                `
              : null}
            ${this.currentStep < this.steps.length
              ? html`
                  <button
                    class="button button-cta"
                    ?disabled=${live(
                      !Object.values(this.validSteps)[
                        layout === 'retail'
                          ? this.currentStep
                          : this.currentStep - 1
                      ],
                    )}
                    @click=${this.nextStep}
                  >
                    ${t('actions.next')}
                  </button>
                `
              : null}
            ${layout === 'wholesale' && this.currentStep === this.steps.length
              ? html`
                  <button
                    class="button button-cta"
                    ?disabled=${live(
                      !Object.values(this.validSteps)[this.steps.length - 1],
                    )}
                    @click=${() => {
                      if (this.configuration && this.quantityValidated) {
                        this.addToCartWithConfiguration();
                      }

                      this.dispatchEvent(
                        new CustomEvent<{ actionEvent: ActionEvent }>(
                          'trigger-validation-events',
                          {
                            composed: true,
                            detail: {
                              actionEvent: 'add-to-cart',
                            },
                          },
                        ),
                      );
                      sendCTAClickEvent('add-to-cart', 'actions.addToCart');
                    }}
                  >
                    ${t('actions.inCart')}
                  </button>
                `
              : null}
          </div>
        `
      : null;
  }

  protected willUpdate() {
    if (this.isMobile) {
      // Disable arrow scrolling for now
      window.addEventListener('keydown', e => {
        if (['ArrowLeft', 'ArrowRight'].includes(e.code)) {
          e.preventDefault();
        }
      });
      // Disable arrow scrolling for now
      window.addEventListener('keyup', e => {
        if (['ArrowLeft', 'ArrowRight'].includes(e.code)) {
          e.preventDefault();
        }
      });
    }
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    buttonStyles,
    css`
      @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX - 1}px) {
        .progress-bar {
          width: 100%;
        }

        :host([part='wholesale']) .progress-bar {
          display: none;
        }

        .progress-bar span {
          display: block;
          width: 100%;
          height: 2px;
          margin-bottom: 10px;
          background-color: var(--fg-primary, var(--fg-fallback-primary));
          transition: width ${ANIMATION_SPEED}s ease-in-out;
        }

        .buttons-wrapper {
          padding: 0 16px;
          max-height: 0;
          opacity: 0;
          height: auto;
          transition:
            max-height ${ANIMATION_SPEED}s ease-in-out ${ANIMATION_SPEED_FAST}s,
            opacity ${ANIMATION_SPEED}s ease-in-out ${ANIMATION_SPEED_FAST}s;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
        }

        :host([part='wholesale']) .buttons-wrapper {
          padding-top: 8px;
          background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
          border-top: 1px solid
            var(--fg-gray--300, var(--fg-fallback-gray--300));
        }

        .previous-button {
          display: flex;
          align-items: center;
        }

        .previous-text {
          color: var(--fg-primary, var(--fg-fallback-primary));
          font-size: 16px;
          font-weight: 500;
          margin-left: 4px;
        }

        .previous-icon {
          fill: var(--fg-primary, var(--fg-fallback-primary));
          color: var(--fg-primary, var(--fg-fallback-primary));
          height: 18px;
          width: 9px;
          display: flex;
        }

        .buttons-wrapper.show {
          max-height: 100px;
          opacity: 1;
        }

        :host([part='wholesale']) .button-cta {
          margin-bottom: 8px;
        }

        .button-cta {
          border: none;
          float: right;
          grid-column: 2;
          margin-bottom: 12px;
        }

        .button-tertiary {
          width: max-content;
          float: left;
          grid-column: 1;
          border: none;
          margin-bottom: 12px;
        }

        .button-link {
          display: flex;
          gap: 4px;
          margin-bottom: 8px;
          align-items: center;
          appearance: none;
          background-color: transparent;
          border: none;
          width: max-content;
          font-size: 1rem;
          color: var(--fg-secondary, var(--fg-fallback-secondary));
          text-decoration: none;
        }

        .button-link svg {
          fill: var(--fg-secondary, var(--fg-fallback-secondary));
          height: 16px;
          width: auto;
        }
      }
    `,
  ];
}
